import { useEffect, forwardRef, useImperativeHandle, useRef } from 'react';

const SpotifyPlayer = forwardRef(({ accessToken, trackUri, onPlayerReady }, ref) => {
  const playerRef = useRef(null); // ✅ useRef to persist player

  useImperativeHandle(ref, () => ({
    pause: () => {
      if (playerRef.current) {
        playerRef.current.pause().then(() => {
          console.log('⏹ Playback paused');
        });
      }
    },
    resume: () => {
      if (playerRef.current) {
        playerRef.current.resume().then(() => {
          console.log('▶ Playback continued');
        });
      }
    },
    restart: () => {
      if (playerRef.current) {
        playerRef.current.seek(0).then(() => {
          console.log('⬤ Playback restarted');
        });
      }
    }
  }));

  useEffect(() => {
    if (!accessToken || !trackUri) return;

    let deviceId;

    const waitForDeviceToBeReady = async (device_id, retries = 10) => {
      for (let i = 0; i < retries; i++) {
        const res = await fetch('https://api.spotify.com/v1/me/player/devices', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await res.json();

        const device = data.devices.find(d => d.id === device_id);
        if (device) {
          console.log(`✅ Device ${device.name} is registered with Spotify`, device);
          return true;
        }

        console.log(`⏳ Waiting for device to appear... (${i + 1})`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.error('❌ Device did not appear in Spotify backend');
      return false;
    };

    const loadSpotifyPlayer = () => {
      return new Promise((resolve, reject) => {
        if (window.Spotify) {
          resolve(window.Spotify);
        } else {
          window.onSpotifyWebPlaybackSDKReady = () => {
            resolve(window.Spotify);
          };

          const existingScript = document.querySelector('script[src="https://sdk.scdn.co/spotify-player.js"]');
          if (!existingScript) {
            const script = document.createElement('script');
            script.src = 'https://sdk.scdn.co/spotify-player.js';
            script.async = true;
            script.onerror = reject;
            document.body.appendChild(script);
          }
        }
      });
    };

    loadSpotifyPlayer()
      .then(() => {
        playerRef.current = new window.Spotify.Player({
          name: 'Hidden Player',
          getOAuthToken: cb => cb(accessToken),
          volume: 0.5,
        });

        playerRef.current.addListener('ready', async ({ device_id }) => {
          console.log('✅ Web Playback SDK ready with device_id:', device_id);
          deviceId = device_id;

          const available = await waitForDeviceToBeReady(deviceId);
          if (!available) return;

          await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uris: [trackUri],
            }),
          });

          console.log('✅ Player connected successfully');
          onPlayerReady(true);
        });

        playerRef.current.addListener('initialization_error', e => window.alert('Init error: ' + e.message));
        playerRef.current.addListener('authentication_error', e => window.alert('Auth error: ' + e.message));
        playerRef.current.addListener('account_error', e => window.alert('Account error: ' + e.message));
        playerRef.current.addListener('playback_error', e => window.alert('Playback error: ' + e.message));

        playerRef.current.connect();
      })
      .catch(err => console.error('❌ Failed to load Spotify SDK:', err));

    return () => {
      if (playerRef.current) {
        playerRef.current.disconnect();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, trackUri]);

  return null;
});

export default SpotifyPlayer;
